import { useMemo } from 'react';

import { Box, Card, Flex, Group, ImageMantine, KebabMenu, Skeleton, Stack, Text, cx } from 'ui';

import { useBreakPoints } from '../../../hooks/useBreakPoints';
import { MyFavoriteCarousel, MyFavoriteCarouselProps } from './MyFavoriteCarousel';
import classes from './MyFavoriteCarouselCard.module.css';

type ConditionalHeaderProps =
  | {
      hideHeader?: true;
      title?: never;
      subtitle?: never;
      logo?: never;
    }
  | {
      hideHeader?: false;
      title?: string;
      subtitle?: string;
      logo?: string;
    };

type MyFavoriteCarouselCardProps = ConditionalHeaderProps &
  MyFavoriteCarouselProps & {
    menuItems?: Array<{
      label: string;
      onClick: () => void;
      withDivider?: boolean;
    }>;
    /**
     * If true, the component will display a loading state
     * @default false
     */
    isLoading?: boolean;
  };

const MyFavoriteCarouselCard = ({
  hideHeader = false,
  title,
  subtitle,
  logo,
  menuItems,
  isLoading = false,
  items,
  ...props
}: MyFavoriteCarouselCardProps) => {
  const { isSmallScreen } = useBreakPoints();

  const renderedSubtitle = useMemo(() => {
    if (isLoading) return <Skeleton h="15px" />;
    if (!subtitle) return null;

    return (
      <Text c="gray.8" fz="xxxs" lh="15px" truncate>
        {subtitle}
      </Text>
    );
  }, [isLoading, subtitle]);

  return (
    <Card className={cx(classes.MyFavoriteCarouselCard, 'MyFavoriteCarouselCard')}>
      <Flex direction="column" gap={{ base: 10, sm: 'md' }}>
        {!hideHeader && (
          <Group justify="space-between" px="md" wrap="nowrap">
            <Group gap="md" wrap="nowrap">
              {logo && (
                <Box className={classes.MyFavoriteCarouselCard__imageContainer}>
                  <ImageMantine
                    className={classes.MyFavoriteCarouselCard__image}
                    src={logo}
                    alt={title}
                  />
                </Box>
              )}
              <Stack gap="0">
                <Text c="gray.8" fz="xs" fw={600} lineClamp={2}>
                  {title}
                </Text>
                {renderedSubtitle}
              </Stack>
            </Group>

            {menuItems && menuItems.length > 0 && (
              <KebabMenu
                buttonProps={{ variant: 'gray', color: 'gray', size: 'sm' }}
                items={menuItems}
              />
            )}
          </Group>
        )}

        {items.length > 0 && (
          <MyFavoriteCarousel isSmallScreen={isSmallScreen} items={items} {...props} />
        )}
      </Flex>
    </Card>
  );
};

export { MyFavoriteCarouselCard };
